import { TablePagination } from "#interfaces/pagination/pagination.interface";
import { ProductListSelect, ProductListSelectCategory, ProductListSelectSubCategory } from "#interfaces/product/product-list-select.interface";

import { ProductService } from "#services/product/product.service";
import { AlertService } from "#utils/alert-service/alert-service.service";
import { RoleChecker } from "#utils/role-checker/role-checker";
import { HttpErrorResponse } from "@angular/common/http";
import { AfterViewChecked, Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, Validators } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { Subject, takeUntil } from "rxjs";

type ProductsWithChecked = ProductListSelect & { checked?: boolean };

@Component({
    selector: "app-products-dialog",
    templateUrl: "./products-dialog.component.html",
    styles: [
        `
                     table {
                       @apply bg-card;
                     }
               
                     thead {
                       @apply dark:bg-card text-gray-500;
                     }
               
                     th {
                       @apply p-4 font-semibold text-md border-b-[1px] border-t-[1px];
                     }
               
                     td {
                       @apply p-4 font-normal border-b-[1px];
                     }
                   `,
    ],
    standalone: false
})
export class ProductsDialogComponent implements OnInit, OnDestroy, AfterViewChecked {
  // States
  public isProductsLoaded = false;
  public isProductsLoadedError = false;
  public isProductsNotFound = false;
  public pagination: TablePagination = { length: 0, page: 0, size: 10 };
  
  // Subscriptions
  public unsubscribe$ = new Subject<void>();
  // Data
  public products: Array<ProductsWithChecked> = [];
  public productsPaginated: Array<ProductsWithChecked> = [];
  public categories: ProductListSelectCategory[] = [];
  public subCategories: ProductListSelectSubCategory[] = [];
  public subCategoriesFiltered: ProductListSelectSubCategory[] = [];
  
  // Forms
  public filterForm = this.formBuilder.group({
    category: [0, [Validators.required]],
    subCategory: new FormControl({ value: 0, disabled: true }, { validators: [Validators.required] }),
  });
  
  public searchForm = new FormControl<string>({ value: '', disabled: false });
  
  // HTML Link
  @ViewChild(MatPaginator) paginator: MatPaginator | null = null;
  
  constructor(
    private _productsService: ProductService,
    private _alertService: AlertService,
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<ProductsDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { filter?: (i: unknown) => boolean; limitSelected: number }) {
    this.data ??= { limitSelected: 10 }
  }
  
  public parseCategories() {
    this.categories = this.products.map((product) => {
      return product.subCategory.category;
    }).filter((subCategory, index, self) => {
      return index === self.findIndex((s) => (
        s.id === subCategory.id
      ));
    });
  }
  
  public parseSubCategories() {
    this.subCategories = this.products.map((product) => {
      return product.subCategory;
    }).filter((subCategory, index, self) => {
      return index === self.findIndex((s) => (
        s.id === subCategory.id
      ));
    });
  }
  
  public addProduct(state: MatCheckboxChange, product: ProductListSelect) {
    if (this.productsSelected.length === this.data.limitSelected && state.checked) {
      return this._alertService.errorAlert([`Solo puedes seleccionar hasta ${this.data.limitSelected} productos a la vez.`], 'Máximo de productos seleccionados alcanzado')
    }
    
    const productSelectedIndex = this.products.findIndex((p) => p.id === product.id);
    this.products[productSelectedIndex].checked = state.checked;
    
    const productPaginatedIndex = this.productsPaginated.findIndex((p) => p.id === product.id);
    this.productsPaginated[productPaginatedIndex].checked = state.checked;
  }
  
  public bulkAddProducts(state: MatCheckboxChange) {
    if (this.productsSelected.length === this.data.limitSelected && state.checked) {
      return this._alertService.errorAlert([`Solo puedes seleccionar hasta ${this.data.limitSelected} productos a la vez.`], 'Máximo de productos seleccionados alcanzado')
    }
    
    this.productsPaginated.slice(0, state.checked ? this.data.limitSelected - this.paginatedProductsSelected.length : this.productsPaginated.length).forEach((product, i) => {
      const productSelectedIndex = this.products.findIndex((p) => p.id === product.id);
      this.products[productSelectedIndex].checked = state.checked;
      this.productsPaginated[i].checked = state.checked;
    });
  }
  
  public get productsSelected() {
    return this.products.filter((product) => product.checked === true);
  }
  
  public get paginatedProductsSelected() {
    return this.productsPaginated.filter((product) => product.checked === true);
  }
  
  public displayWithCategory = (categoryId: number) => {
    if (!categoryId) return 'Todas las categorías';
    
    return this.categories.find((category) => category.id === categoryId)?.name ?? 'Categoría no encontrada';
  }
  
  public displayWithSubCategory = (subCategoryId: number) => {
    if (!subCategoryId) return 'Todas las subcategorías';
    
    return this.subCategories.find((subCategory) => subCategory.id === subCategoryId)?.name ?? 'Subcategoría no encontrada';
  }
  
  public unMarkAll() {
    this.products = this.products.map((product) => ({ ...product, checked: false }));
    this.productsPaginated = this.productsPaginated.map((product) => ({ ...product, checked: false }));
  }
  
  public submit() {
    return this.dialogRef.close(this.productsSelected);
  }
  
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  
  ngOnInit(): void {
    this._productsService.getListSelect('', 0, 0, true).subscribe({
      next: (response) => {
        this.isProductsLoaded = true;
        this.products = this.data?.filter ? response.data.filter(this.data.filter).sort((a, b) => b.currentStock - a.currentStock) : response.data.sort((a, b) => b.currentStock - a.currentStock);;
        this.productsPaginated = response.data.slice(0, this.pagination.size);
        this.pagination.length = response.totalItems;
        
        this.parseCategories();
        this.parseSubCategories();
      },
      error: (err: HttpErrorResponse) => {
        this.isProductsLoadedError = true;
        this._alertService.errorAlert(err.error.Errors, err.error.Message);
      }
    });
    
    this.filterForm.controls.category.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((categoryId) => {
      if (categoryId) {
        this.filterForm.controls.subCategory.enable();
        this.filterForm.controls.subCategory.setValue(0);
      } else {
        this.filterForm.controls.subCategory.disable();
        this.filterForm.controls.subCategory.setValue(0);
      }
      
      this.productsPaginated = this.products.slice(0, this.pagination.size).filter((product) => {
        if (categoryId) {
          return product.subCategory.category.id === categoryId;
        }
        
        return true;
      });
      
      this.subCategoriesFiltered = this.subCategories.filter((subCategory) => subCategory.category.id === categoryId);
    });
    
    this.filterForm.controls.subCategory.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((subCategoryId) => {
      if (subCategoryId) {
        this.productsPaginated = this.products.slice(0, this.pagination.size).filter((product) => {
          return product.subCategory.id === subCategoryId;
        });
      } else {
        this.productsPaginated = this.products.slice(0, this.pagination.size).filter((product) => {
          return product.subCategory.category.id === this.filterForm.controls.category.value;
        });
      }
    });
    
    this.searchForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((term) => {
      const { category, subCategory } = this.filterForm.controls;
      
      if (!term) {
        const result = this.products.filter((product) => {
          if (category.value && subCategory.value) {
            return product.subCategory.id === subCategory.value;
          } else if (category.value) {
            return product.subCategory.category.id === category.value;
          }
          
          return true;
        });
        
        this.productsPaginated = result.slice(0, this.pagination.size);
        this.pagination.length = result.length;
        this.isProductsNotFound = result.length === 0;
      }
      
      if (term) {
        const result = this.products.filter((product) => {
          if (category.value && subCategory.value) {
            return product.subCategory.id === subCategory.value && (product.name.trim().toLowerCase().includes(term.toLowerCase().trim()) || product.code.trim().toLowerCase().includes(term.toLowerCase().trim()));
          } else if (category.value) {
            return product.subCategory.category.id === category.value && (product.name.trim().toLowerCase().includes(term.toLowerCase().trim()) || product.code.trim().toLowerCase().includes(term.toLowerCase().trim()));
          }
          
          return product.name.trim().toLowerCase().includes(term.toLowerCase().trim()) || product.code.toLowerCase().includes(term.toLowerCase().trim());
        });
        
        this.productsPaginated = result.slice(0, this.pagination.size);
        this.pagination.length = result.length;
        this.isProductsNotFound = result.length === 0;
      }
    });
  }
  
  ngAfterViewChecked(): void {
    this.paginator.page.pipe(takeUntil(this.unsubscribe$)).subscribe((page) => {
      const { category, subCategory } = this.filterForm.controls;
      const search = this.searchForm.value;
      
      if (category.value && subCategory.value) {
        this.productsPaginated = this.products.slice(page.pageIndex * page.pageSize, (page.pageIndex + 1) * page.pageSize).filter((product) => {
          return product.subCategory.id === subCategory.value && (product.name.trim().toLowerCase().includes(search.toLowerCase().trim()) || product.code.trim().toLowerCase().includes(search.toLowerCase().trim()));
        });
      } else if (category.value) {
        this.productsPaginated = this.products.slice(page.pageIndex * page.pageSize, (page.pageIndex + 1) * page.pageSize).filter((product) => {
          return product.subCategory.category.id === category.value && (product.name.trim().toLowerCase().includes(search.toLowerCase().trim()) || product.code.trim().toLowerCase().includes(search.toLowerCase().trim()));
        });
      } else {
        this.productsPaginated = this.products.filter((product) => product.name.trim().toLowerCase().includes(search.toLowerCase().trim()) || product.code.trim().toLowerCase().includes(search.toLowerCase().trim())).slice(page.pageIndex * page.pageSize, (page.pageIndex + 1) * page.pageSize);
      }
    });
  }

  get canViewInventory(): boolean {
    return RoleChecker("SuperAdmin") || RoleChecker("Admin") || RoleChecker("Manager");
  }
}
